import { TriangleAlert } from 'lucide-react';

import type { PatientGateIssue } from '../model/types';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';

export interface PatientEligibilityWarningDialogProps {
  open: boolean;
  issues: PatientGateIssue[];
  isSuggesting: boolean;
  onOpenChange: (nextOpen: boolean) => void;
  onContinue: () => void;
  onSuggestNext: () => void;
}

export function PatientEligibilityWarningDialog({
  open,
  issues,
  isSuggesting,
  onOpenChange,
  onContinue,
  onSuggestNext,
}: PatientEligibilityWarningDialogProps) {
  const titleId = 'patient-eligibility-warning-title';
  const descriptionId = 'patient-eligibility-warning-description';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="border-red-500/60 bg-red-950/90 text-red-100 shadow-[0_0_40px_rgba(239,68,68,0.15)]"
      >
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
              <TriangleAlert className="size-5 text-red-400" />
            </div>
            <div>
              <DialogTitle id={titleId} className="text-red-50">
                You are not eligible to view this patient
              </DialogTitle>
              <DialogDescription id={descriptionId} className="text-red-300/80">
                One or more eligibility requirements have not been met. You may still continue, but
                please review the violations below.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {issues.length > 0 ? (
          <div className="rounded-md border border-red-500/30 bg-red-900/50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-400">
              Violations
            </p>
            <ul className="space-y-1.5 text-sm">
              {issues.map((issue) => (
                <li
                  key={`${issue.code}:${issue.title}:${issue.detail}`}
                  className="flex items-start gap-2"
                >
                  <span className="mt-1.5 block size-1.5 shrink-0 rounded-full bg-red-400" />
                  <span>
                    <span className="font-medium text-red-100">{issue.title}:</span>{' '}
                    <span className="text-red-300">{issue.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onContinue}
            className="border-red-500/30 bg-red-900/60 text-red-200 hover:bg-red-800/60"
          >
            Continue viewing patient
          </Button>
          <Button
            type="button"
            onClick={onSuggestNext}
            disabled={isSuggesting}
            className="bg-red-600 text-white hover:bg-red-500"
          >
            {isSuggesting ? 'Finding…' : 'Move to next actionable patient'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
